import { createFileRoute, getRouteApi } from '@tanstack/react-router';
import { createColumnHelper } from '@tanstack/react-table';
import { LANGUAGE_LABELS, type Language } from '@/admin/languages';
import { ChaptersToolbar } from '@/admin/publishing/ChaptersToolbar';
import { createTablePage } from '@/admin/ui/createTablePage';
import { readTableSearch } from '@/admin/ui/tableSearch';
import { formatWhen } from '@/admin/ui/Versions';
import api from '@/api';
import type { components } from '@/api/qraft/schema';
import i18n from '~/i18n';

type AdminBibleChapterSummary =
  components['schemas']['AdminBibleChapterSummary'];

// Optional keys, so a bare `<Link to="/admin/bible/">` needs no `search`.
type BibleSearch = { language?: Language; book?: number; page?: number };

const routeApi = getRouteApi('/admin/bible/');
const LANGUAGE_CODES = Object.keys(LANGUAGE_LABELS) as Language[];

const columnHelper = createColumnHelper<AdminBibleChapterSummary>();

const columns = [
  columnHelper.accessor('book_name', {
    header: () => i18n.t('admin.publishing.bible.book'),
  }),
  columnHelper.accessor('chapter_number', {
    header: () => i18n.t('admin.publishing.bible.chapter'),
  }),
  columnHelper.accessor('title', {
    header: () => i18n.t('admin.common.title'),
  }),
  columnHelper.accessor('block_count', {
    header: () => i18n.t('admin.publishing.bible.blocks'),
  }),
  columnHelper.accessor('updated_at', {
    header: () => i18n.t('admin.publishing.bible.updated'),
    cell: (c) => formatWhen(c.getValue()),
  }),
];

const ChapterList = createTablePage<
  AdminBibleChapterSummary,
  '/admin/bible/$chapterId'
>({
  name: 'admin.nav.bible',
  urlState: true,
  useData: (_page, _search, extras) => {
    // Cast: the route names this component, so inferring its search is circular.
    const { language, book } = routeApi.useSearch() as BibleSearch;
    return api.adminListBibleChapters.useQuery({
      query: {
        language: language ?? 'EN',
        book_id: book,
        limit: extras.paging.limit,
        offset: extras.paging.offset,
      },
    });
  },
  columns,
  itemBaseUrl: '/admin/bible/$chapterId',
  getParams: (row) => ({ chapterId: String(row.id) }),
  actions: <ChaptersToolbar />,
});

export const Route = createFileRoute('/admin/bible/')({
  staticData: { minTier: 'ADMIN' },
  validateSearch: (s: Record<string, unknown>): BibleSearch => {
    const { page } = readTableSearch(s);
    const book = Number(s.book);
    return {
      language: LANGUAGE_CODES.find((l) => l === s.language),
      book: Number.isInteger(book) && book > 0 ? book : undefined,
      page: page > 0 ? page + 1 : undefined,
    };
  },
  component: ChapterList,
});
